/**
 * One month's statement, read whole.
 *
 * `renderedText` IS THE DOCUMENT AND IT IS SERVED WHOLE. It "was rendered once, at the time, and
 * it is never parsed or queried into" — rebuilding a statement from the run would use today's
 * template, so the stored text is the only faithful answer to "what did I receive?". This panel
 * therefore prints it as a paragraph and never picks fields out of it.
 *
 * THERE IS NO PDF. The prototype's payout table offers "Statement (PDF)"; the contract serves this
 * address as `application/json` OR `text/csv` by content negotiation and declares no PDF anywhere.
 * So the export here is CSV, from the same URL.
 *
 * AN AWARD LINE SETTLES AT THE AWARD ALONE. `settledAmountMinor` on an `award` line is the award
 * and never the award plus the article's computed fee — and the computed fee is still shown,
 * because it is kept as a ranking input and an author who sees it missing will ask why.
 *
 * ORDINAL 1 EARNS NOTHING, EVER. An `excluded_first_article` line settles at zero on purpose, and
 * the panel says so rather than leaving a bare 0.00 to be read as a bug. Its words hold for an
 * author brought over from the old apps too (design 14, F2): the import numbered the old articles,
 * so their #1 IS their first article and the pay rule is unchanged. A statement line carries no
 * `importedFromOldApp`, and the screen would not say so if it did (design 15: no "old app" words in
 * front of an author).
 *
 * THE TOTALS NEVER CALL THE PAYOUT "CARRIED FORWARD" OR "SENT" (design 14, F14).
 * `closingBalanceMinor` is opening + earnings + award (`payout_run_item_check`) - the total BEFORE
 * the payout, which on a payable run is the money the payout takes. Labelled "Carried forward" it
 * stood beside "Sent to you" with the same $55.33 in both. It is the "Total"; `amountMinor` is the
 * "Payout", a figure set when the run was worked out, so it says nothing about whether money moved
 * (the statement carries no `paidAt` - the runs table on the same page does); and "rolls over"
 * is said only when there is no payout.
 *
 * IN THE PROTOTYPE'S LOOK, IN PLAIN WORDS (design 15, the owner's live look at real cabinets,
 * 2026-10-01). The panel opens in place under its month in the Statements list card, so its title
 * is an h3 under that section's h2; the totals are a row of small figures; no line names an
 * article by its id (a line with no ordinal is "An article"); and the words say what an author
 * reads - "article earnings", "kept exactly as it was written" - not how the platform stores it.
 */
import { Alert, Button, Card, Divider, Group, Stack, Table, Text, Title } from '@mantine/core';
import { Download } from '@moonx/ui';
import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useCabinetApi } from '../../features/cabinet/api';
import { ErrorState, LoadingState, Money } from '../../features/cabinet/components';
import { useAction, useAsync } from '../../features/cabinet/state';
import { formatCount, formatInstant, formatPeriod } from '../../features/cabinet/format';
import type { StatementLine } from '../../features/cabinet/api';
import { useTranslation } from '../../i18n';
import classes from './payments.module.css';

/** The settlements the catalogue words (`statement.settlement.<kind>`). */
const SETTLEMENTS: ReadonlySet<string> = new Set<StatementLine['settlement']>([
  'earning',
  'award',
  'excluded_first_article',
  'paid_in_house',
]);

function isSettlement(kind: string): kind is StatementLine['settlement'] {
  return SETTLEMENTS.has(kind);
}

export function StatementPanel({ period, onClose }: { period: string; onClose: () => void }) {
  const { t } = useTranslation('payments');
  const api = useCabinetApi();
  const month = formatPeriod(period);

  const read = useCallback(
    (signal: AbortSignal) => api.getStatement(period, signal),
    [api, period],
  );
  const statement = useAsync(read, `statement:${period}`);

  const download = useAction(async () => {
    const csv = await api.getStatementCsv(period);
    // The CSV representation of the SAME address. A blob and a click is the only way a browser can
    // hand a file over from a bearer-authenticated GET — a plain link would send no token.
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `moonx-statement-${period}.csv`;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    return csv;
  });

  return (
    <Card padding="lg" withBorder component="section" aria-label={t('statement.label', { month })}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Title order={3} size="h4">
            {month}
          </Title>
          <Group gap="xs">
            <Button
              variant="default"
              size="compact-sm"
              leftSection={<Download aria-hidden="true" size={14} />}
              loading={download.isRunning}
              disabled={statement.data === undefined}
              onClick={() => {
                void download.run();
              }}
            >
              {t('statement.download')}
            </Button>
            <Button variant="subtle" size="compact-sm" onClick={onClose}>
              {t('statement.close')}
            </Button>
          </Group>
        </Group>

        {download.error === undefined ? null : (
          <ErrorState title={t('statement.downloadFailed')} error={download.error} />
        )}

        {statement.status === 'loading' && statement.data === undefined ? (
          <LoadingState label={t('statement.reading', { month })} rows={3} />
        ) : statement.status === 'error' && statement.data === undefined ? (
          <ErrorState
            title={t('statement.failed')}
            error={statement.error}
            onRetry={statement.refetch}
          />
        ) : statement.data === undefined ? null : (
          <Stack gap="md">
            <Alert variant="light" role="note" title={t('statement.asWritten')}>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {statement.data.renderedText}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                {t('statement.writtenAt', { when: formatInstant(statement.data.renderedAt) })}
              </Text>
            </Alert>

            <Divider label={t('statement.byArticle')} labelPosition="left" />

            {statement.data.lines.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t('statement.noArticles')}
              </Text>
            ) : (
              <div
                className={classes.scroller}
                tabIndex={0}
                role="region"
                aria-label={t('statement.articlesLabel', { month })}
              >
                <Table verticalSpacing="sm" horizontalSpacing="md" style={{ minWidth: 720 }}>
                  <Table.Caption>{t('statement.caption', { month })}</Table.Caption>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th scope="col">{t('statement.columns.article')}</Table.Th>
                      <Table.Th scope="col" style={{ textAlign: 'right' }}>
                        {t('statement.columns.viewsCounted')}
                      </Table.Th>
                      <Table.Th scope="col" style={{ textAlign: 'right' }}>
                        {t('statement.columns.likes')}
                      </Table.Th>
                      <Table.Th scope="col" style={{ textAlign: 'right' }}>
                        {t('statement.columns.comments')}
                      </Table.Th>
                      <Table.Th scope="col" style={{ textAlign: 'right' }}>
                        {t('statement.columns.articleEarnings')}
                      </Table.Th>
                      <Table.Th scope="col" style={{ textAlign: 'right' }}>
                        {t('statement.columns.settled')}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {statement.data.lines.map((line) => (
                      <Table.Tr key={line.articleId}>
                        <Table.Th scope="row" style={{ fontWeight: 400 }}>
                          <Stack gap={2}>
                            <Text size="sm">
                              {line.publicationOrdinal === undefined ||
                              line.publicationOrdinal === null
                                ? t('statement.anArticle')
                                : t('statement.yourArticle', {
                                    number: String(line.publicationOrdinal),
                                  })}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {isSettlement(line.settlement)
                                ? t(`statement.settlement.${line.settlement}`)
                                : null}
                            </Text>
                          </Stack>
                        </Table.Th>
                        <NumericCell>
                          {line.countableViews === undefined
                            ? '—'
                            : formatCount(line.countableViews)}
                        </NumericCell>
                        <NumericCell>
                          {line.countableLikes === undefined
                            ? '—'
                            : formatCount(line.countableLikes)}
                        </NumericCell>
                        <NumericCell>
                          {line.countableComments === undefined
                            ? '—'
                            : formatCount(line.countableComments)}
                        </NumericCell>
                        <NumericCell>
                          {line.computedAmountMinor === undefined ? (
                            '—'
                          ) : (
                            <Money minor={line.computedAmountMinor} currency={line.currency} />
                          )}
                        </NumericCell>
                        <NumericCell>
                          <Money
                            minor={line.settledAmountMinor}
                            currency={line.currency}
                            fw={700}
                            emphasiseNegative
                          />
                        </NumericCell>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}

            <Group gap="xl" wrap="wrap">
              <TotalsItem
                label={t('statement.totals.fromEarlier')}
                minor={statement.data.totals.openingBalanceMinor}
                currency={statement.data.currency}
              />
              <TotalsItem
                label={t('statement.totals.articleEarnings')}
                minor={statement.data.totals.earningsMinor}
                currency={statement.data.currency}
              />
              <TotalsItem
                label={t('statement.totals.award')}
                minor={statement.data.totals.awardMinor}
                currency={statement.data.currency}
              />
              <TotalsItem
                label={t('statement.totals.total')}
                minor={statement.data.totals.closingBalanceMinor}
                currency={statement.data.currency}
              />
              <TotalsItem
                label={t('statement.totals.payout')}
                minor={statement.data.totals.amountMinor}
                currency={statement.data.currency}
                absentNote={t('statement.totals.noPayout')}
              />
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function NumericCell({ children }: { children: ReactNode }) {
  return (
    <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </Table.Td>
  );
}

function TotalsItem({
  label,
  minor,
  currency,
  absentNote,
}: {
  label: string;
  minor: number | null | undefined;
  currency: string;
  absentNote?: string;
}) {
  const { t } = useTranslation('payments');
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: 'var(--mx-ls-eyebrow)' }}>
        {label}
      </Text>
      {minor === undefined || minor === null ? (
        <Text size="sm" c="dimmed">
          {absentNote ?? t('statement.totals.notRecorded')}
        </Text>
      ) : (
        <Money minor={minor} currency={currency} fw={700} emphasiseNegative />
      )}
    </Stack>
  );
}
